import { useCallback, useEffect, useState } from 'react';
import { useGetTariffs } from '~/src/services/api/api-content/cities/useGetTariffsInternet';
import { usePrepareTariff } from '~/src/components/LandingBlock/Tariffs/usePrepareTariffs';
import { formatBundleFull } from '~/src/services/tariffs/formatBundleFull';
import { formatMonoFull } from '~/src/services/tariffs/formatMonoFull';
import { getTextWithTag } from '~/src/helpers/getTextWithTag';
import type { TCardButtonProps } from '~/src/components/LandingBlock/TariffCardsRA/CardRA';
import type { ITariffFull } from '~/src/services/tariffs/types';
import * as ST from './styled';
import ChannelListPopup from './ChannelListPopup';
import TariffsRenderer from './TariffsRenderer';

interface IProps {
    cityDomain: string,
    blockId: string,
    buttonProps?: TCardButtonProps,
    customStyles?: Record<string, any>,
    showBundles: boolean,
    isCarouselActive?: boolean,
    isFullByActive: boolean,
    bundlesAliases: string,
    showInternet: boolean,
    internetAliases: string,
    aliasWildcard: string,
    title: string,
    additionalBlockConfig?: any,
    tag?: string;
}

const Tariffs = ({
    cityDomain = 'perm',
    blockId,
    buttonProps,
    customStyles,
    showBundles,
    showInternet,
    bundlesAliases,
    internetAliases,
    aliasWildcard,
    isCarouselActive,
    isFullByActive,
    title,
    additionalBlockConfig,
    tag,
}: IProps): JSX.Element => {
    const [selectedTariff, setSelectedTariff] = useState<ITariffFull | null>(null);

    const [, { bundles = undefined, mono = undefined } = {}, getTariffs] = useGetTariffs();

    useEffect(() => {
        getTariffs();
    }, [getTariffs]);

    const getFilteredTariffs = useCallback((tariffs: ITariffFull[], aliases: string) => {
        const filters = aliases.split(',');

        return filters.reduce((acc: ITariffFull[], filter) => {
            const matchingTariff = tariffs.find(tariff => {
                if (aliasWildcard !== '' && tariff.alias) {
                    if (tariff.alias.search(aliasWildcard) !== -1 && !tariff.isCottage) {
                        return true;
                    }
                }

                return tariff.alias === filter;
            });

            if (matchingTariff) {
                acc.push(matchingTariff);
            }

            return acc;
        }, []);
    }, [aliasWildcard]);

    const { bundles: filteredBundles, mono: filteredMono } = usePrepareTariff<ITariffFull[]>({
        bundles: bundles?.map(formatBundleFull),
        internet: mono?.map(formatMonoFull),
        getFilteredTariffs,
        bundlesAliases,
        internetAliases,
    });

    const titleWithTag = getTextWithTag(title, tag);

    const handleOpenModal = (tariff: ITariffFull) => {
        setSelectedTariff(tariff);
    };

    return (
        <ST.Tariffs id={blockId} {...customStyles} {...additionalBlockConfig}>
            <ST.Container modeName='wide'>
                <ST.Title {...customStyles} dangerouslySetInnerHTML={{ __html: titleWithTag }} />
                {showBundles &&
                    <ST.CardList>
                        <TariffsRenderer
                            tariffs={filteredBundles}
                            isCarouselActive={isCarouselActive}
                            isFullByActive={isFullByActive}
                            buttonProps={buttonProps}
                            cityDomain={cityDomain}
                            handleOpenModal={handleOpenModal}
                        />
                    </ST.CardList>
                }
                {showInternet &&
                    <ST.CardList>
                        <TariffsRenderer
                            tariffs={filteredMono}
                            isCarouselActive={isCarouselActive}
                            isFullByActive={isFullByActive}
                            buttonProps={buttonProps}
                            cityDomain={cityDomain}
                            handleOpenModal={handleOpenModal}
                        />
                    </ST.CardList>
                }
                {selectedTariff && (
                    <ChannelListPopup
                        freePackets={selectedTariff.freePackets}
                        onClose={() => setSelectedTariff(null)}
                    />
                )}
            </ST.Container>
        </ST.Tariffs>
    );
};

export default Tariffs;
